import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { firstValueFrom } from 'rxjs';
import { IAppState } from '../../core/core.state';
import { LoadingSpinnerService } from '../../core/data/loading-spinner.service';
import { ICurrentBoard } from '../../core/models/current-board.model';
import { selectUserDetails } from '../../core/store/store.selectors';
import { BoardService } from '../../shared/services/board.service';
import { environment } from '../../../environments/environment';

/**
 * Demotes a Pattern Board to one of the admin's own boards. The edit endpoint ignores a changed
 * user_id, so the board cannot be reassigned in place: instead a copy is created under the admin's
 * email and the shared original is deleted. The board leaves the Pattern Boards tab and appears
 * under My Boards.
 *
 * Closes with the new board on success, or undefined when dismissed.
 */
@Component({
    selector: 'lynkd-pattern-dialog-board-demote',
    templateUrl: './dialog-board-demote.component.html',
    styleUrls: ['./dialog-board-demote.component.scss']
})
export class DialogBoardDemoteComponent {
    public useBrandivioBranding: boolean = environment.useBrandivioBranding;

    public constructor(
        @Inject(MAT_DIALOG_DATA) public data: { boardData: ICurrentBoard },
        public dialogRef: MatDialogRef<DialogBoardDemoteComponent, ICurrentBoard>,
        private readonly _boardService: BoardService,
        private readonly _loadingSpinner: LoadingSpinnerService,
        private readonly _snackBar: MatSnackBar,
        private readonly _store: Store<IAppState>
    ) {}

    public async demoteBoard(): Promise<void> {
        this._loadingSpinner.spin$.next(true);

        let demotedBoard: ICurrentBoard;

        // The copy is created first: if it fails, the shared board is still intact and the admin
        // has lost nothing. Deleting first would risk destroying the only copy.
        try {
            demotedBoard = await firstValueFrom(
                this._boardService.copyBoardTo(this.data.boardData, await this.getCurrentUserEmail())
            );
        } catch (e) {
            this._snackBar.open('Board demotion failed', 'Close', {
                duration: 5000
            });
            this._loadingSpinner.spin$.next(false);

            return;
        }

        try {
            await firstValueFrom(this._boardService.deleteBoard(String(this.data.boardData.board_id)));

            this._snackBar.open('This board has been moved to My Boards', 'Close', {
                duration: 5000
            });
        } catch (e) {
            // The copy exists, so the demotion half-succeeded. Say so rather than reporting a
            // clean success -- the board is now in both places until the shared one is removed.
            this._snackBar.open(
                `This board was copied to My Boards, but could not be removed from ${
                    this.useBrandivioBranding ? 'Company' : 'Pattern'
                } boards`,
                'Close',
                { duration: 8000 }
            );
        } finally {
            this._loadingSpinner.spin$.next(false);
        }

        this.dialogRef.close(demotedBoard);
    }

    /**
     * The logged-in user's email, matching the identity boards are stored and queried under.
     * Prefers the store (populated at login from the Cognito email attribute) and falls back to
     * the id token's email claim, the same source the dashboard uses to hydrate it.
     */
    private async getCurrentUserEmail(): Promise<string> {
        const userDetails: { email: string } = await firstValueFrom(this._store.select(selectUserDetails));

        if (userDetails?.email) {
            return userDetails.email;
        }

        const session: CognitoUserSession = await Auth.currentSession();

        return session.getIdToken().payload.email as string;
    }
}
